import type { AccessStatus, AppUserRow, IdhubSync } from '../../api/types';
import { formatDateTime } from '../../lib/format';

export type UserAction = 'approve' | 'deny' | 'revoke' | 'makeAdmin' | 'removeAdmin';

/** Body of PATCH /api/users/:id for each action. */
export const ACTION_PATCH: Record<UserAction, { access?: Exclude<AccessStatus, 'pending'>; isAdmin?: boolean }> = {
  approve: { access: 'approved' },
  deny: { access: 'denied' },
  revoke: { access: 'denied' },
  makeAdmin: { isAdmin: true },
  removeAdmin: { isAdmin: false },
};

export const ACTION_LABEL: Record<UserAction, string> = {
  approve: 'Aprobar',
  deny: 'Denegar',
  revoke: 'Retirar acceso',
  makeAdmin: 'Hacer administrador',
  removeAdmin: 'Quitar administrador',
};

/** Actions that take something away ask for confirmation first. */
export const NEEDS_CONFIRMATION = new Set<UserAction>(['deny', 'revoke', 'removeAdmin']);

/**
 * What an administrator can do with a user. Nobody changes their own access
 * (so nobody locks themselves out) and ADMIN_EMAILS cannot be changed here,
 * the same rules the API enforces.
 */
export function availableActions(user: AppUserRow, myId: number): UserAction[] {
  if (user.id === myId || user.fixedAdmin) return [];
  if (user.access === 'pending') return ['approve', 'deny'];
  if (user.access === 'denied') return ['approve'];
  return [user.isAdmin ? 'removeAdmin' : 'makeAdmin', 'revoke'];
}

export function groupByAccess(users: AppUserRow[]): Record<AccessStatus, AppUserRow[]> {
  const groups: Record<AccessStatus, AppUserRow[]> = { pending: [], approved: [], denied: [] };
  for (const user of users) groups[user.access].push(user);
  return groups;
}

/** How the request arrived: announced by IDHub, or the person's first sign-in. */
export function requestDetail(user: AppUserRow): string {
  if (user.requestedAt) return `Acceso dado en IDHub el ${formatDateTime(user.requestedAt)}`;
  if (user.firstLoginAt) return `Entró por primera vez el ${formatDateTime(user.firstLoginAt)}`;
  return '';
}

/** A warning to show after a decision when IDHub did not follow it, or null. */
export function idhubNotice(action: UserAction, sync: IdhubSync, name: string): string | null {
  const granting = action === 'approve';
  if (granting && sync === 'not_found') {
    return `${name} ya está aprobado en Performance, pero IDHub ya no tenía su solicitud pendiente (ya estaba aceptada, caducó a las 72 h o se retiró). Si no puede entrar, vuelve a darle acceso en IDHub: entrará directamente.`;
  }
  if (!granting && sync === 'unavailable') {
    return `${name} ya no puede ver datos en Performance, pero IDHub no ha respondido y su acceso allí sigue activo. Retíralo también desde el panel de IDHub.`;
  }
  return null;
}

export const displayName = (user: { name: string | null; email: string }) => user.name?.trim() || user.email;
